/*
* The abstract class FirestoreDocument is a base class for all classes that need to be saved to(fetched from) Firestore.
*/

import { Timestamp, DocumentReference, doc, setDoc, collection, getDoc } from 'firebase/firestore';
import { fs } from '../firebaseConfig';

export abstract class FirestoreDocument {
  abstract Id: string | null;

  protected abstract get collectionName(): string;

  protected toFirestoreObject(id:string): Object {
    return Object.keys(this).reduce((obj, key) => {
      const value = (this as any)[key];
      if (value !== undefined && typeof value !== 'function') {
        (obj as any)[key] = value;
      }
      obj['Id'] = id;

      return obj;
    }, {});
  }

  async saveToFirestore(): Promise<DocumentReference> {

    const docId = this.Id || "";    
    const docRef = docId ? 
      doc(fs, this.collectionName, docId) : 
      doc(collection(fs, this.collectionName));

    const docData = this.toFirestoreObject(docRef.id);    
    docData['UpdatedAt'] = Timestamp.now();
    
    if(!this.Id) {
      docData['CreatedAt'] = Timestamp.now();
      this.Id = docRef.id;
    }
    
    await setDoc(docRef, docData, { merge: true });

    return docRef;
  }

  async setMergeToFirestore(data: any): Promise<DocumentReference> {
      
      const docId = this.Id || "";

      if(!docId) {
        throw new Error('Id is required to setMerge document to Firestore');
      }
      const docRef = doc(fs, this.collectionName, docId);
  
      await setDoc(docRef, {...data, UpdatedAt: Timestamp.now()}, { merge: true });
  
      return docRef;
    }

  /*
   * Apply an already-fetched document's data, exactly as getFromFirestore would.
   * Lets a caller that resolved the doc in a batch reuse it instead of issuing a
   * second single-doc read (see helpers/productDataCache.ts).
   */
  hydrate(data: any): void {
    if (!data) return;
    Object.keys(data).forEach(key => {
      (this as any)[key] = data[key];
    });
  }

  async getFromFirestore(): Promise<void> {
    if (!this.Id) {
      throw new Error('Id is required to get document from Firestore');
    }

    const docId = this.Id || "";    
    const docRef = doc(fs, this.collectionName, docId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      Object.keys(data).forEach(key => {
        (this as any)[key] = data[key];
      });
    }
  }
}